import cx from 'clsx';
import { Button, Icon, IconButton } from '@vechaiui/react';
import { FieldArray as BaseFieldArray, useField } from 'formik';
import { PlusCircleIcon, XIcon } from '@heroicons/react/outline';

const FieldArray: React.FC<FieldArrayProps> = ({
  children,
  initialValue,
  isDisabled,
  isReadOnly,
  label,
  name,
  variant,
  ...props
}) => {
  const [arrayField] = useField<any[] | undefined>(name);

  return (
    <BaseFieldArray {...props} name={name}>
      {(arrayHelpers) => (
        <div>
          <div
            className={cx(
              variant === 'compact' && 'divide-y space-y-4',
              variant === 'normal' && 'space-y-8 divide-y',
            )}
          >
            {arrayField.value?.map((_, index) => (
              <div
                className={cx(
                  'space-y-2',
                  index > 0 && variant === 'compact' && 'pt-3',
                  index > 0 && variant === 'normal' && 'pt-8',
                )}
                key={`${name}-${index}`}
              >
                {variant === 'normal' && (
                  <div className="flex items-center justify-between">
                    <label>
                      {label} #{index + 1}
                    </label>

                    {!isDisabled && !isReadOnly && (
                      <IconButton
                        onClick={() => {
                          arrayHelpers.remove(index);
                        }}
                      >
                        <Icon as={XIcon} label="delete" color="red" className="w-4 h-4" />
                      </IconButton>
                    )}
                  </div>
                )}

                <div className="flex space-x-4 items-center">
                  <div className="flex-1 space-y-3">
                    {children({
                      isDisabled,
                      isReadOnly,
                      name: `${name}.${index}`,
                    })}
                  </div>

                  {variant === 'compact' && !isDisabled && !isReadOnly && (
                    <IconButton
                      onClick={() => {
                        arrayHelpers.remove(index);
                      }}
                    >
                      <Icon as={XIcon} label="delete" color="red" className="w-4 h-4" />
                    </IconButton>
                  )}
                </div>
              </div>
            ))}
          </div>

          {!isDisabled && !isReadOnly && (
            <footer className={cx(arrayField.value && arrayField.value.length > 0 && 'mt-6')}>
              <Button
                className="space-x-2"
                onClick={() => {
                  arrayHelpers.push(initialValue);
                }}
                variant="ghost"
                leftIcon={
                  <Icon as={PlusCircleIcon} label="add" className="w-5 h-5 text-primary-500" />
                }
                size="lg"
                type="button"
              >
                <span>Add {label.toLowerCase()}</span>
              </Button>
            </footer>
          )}
        </div>
      )}
    </BaseFieldArray>
  );
};
export type FieldArrayProps<T = any> = Pick<
  React.ComponentProps<typeof BaseFieldArray>,
  'name' | 'validateOnChange'
> & {
  children: (props: {
    isDisabled?: boolean;
    isReadOnly?: boolean;
    name: string;
  }) => React.ReactNode;
  initialValue: T;
  isDisabled?: boolean;
  isReadOnly?: boolean;
  label: string;
  variant?: 'compact' | 'normal';
};
FieldArray.defaultProps = {
  variant: 'normal',
};

export default FieldArray;
